import React, { useEffect, useMemo, useState, useRef, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

import { Card } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import { Badge } from 'components/ui/badge';
import { Icon, type IconName } from 'components/ui/icon';
import { cn } from 'lib/utils';
import PageHeading from 'components/heading';
import ToastContainer, { ToastType } from 'components/toasts/container';

import accessLocalStorage from 'services/localStorage';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import usePosConfig from 'state/posConfig/hooks/usePosConfig';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import useVirtualTerminal from 'state/virtualTerminal/hooks/useVirtualTerminal';
import useUnpaidTickets from 'state/ticket/hooks/useUnpaidTickets';
import useQuery from 'utils/hooks/useQuery';
import QUERY from 'utils/query';
import STRING from 'utils/string';

import { VIRTUAL_TERMINAL } from 'state/virtualTerminal/type';
import { POS_CONFIG } from 'state/posConfig/type';

import PaymentCollectionDrawer, {
  IVirtualTerminalForm,
} from './payment-collection';
import {
  generateRandomAlphaNum,
  generateUUID,
  isNonSTRefId,
} from './paymentDataUtil';
import GuestListTab from './guestList';
import UnpaidTicketsTab from './unpaidTickets';
import RecentPayments from './recent-payments';
import CheckoutTab from './checkoutTab';
import Restricted from './Restricted';

type TabKey = 'checkout' | 'guest list' | 'unpaid tickets' | 'recent payments';

interface VtTabConfig {
  value: TabKey;
  label: string;
  iconName?: IconName;
  show: boolean;
  count?: number;
  render: () => ReactNode;
}

const formatPhoneNumber = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length >= 10) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(
      6,
      10
    )}`;
  }
  return value;
};

const VirtualTerminals = (): JSX.Element => {
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToast, setErrorToast] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successToast, setSuccessToast] = useState('');
  const successToastTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { posConfig, isLoading, toFetchGuestList, toFetchEnabledProviders } =
    usePosConfig();
  const { paymentConfig, toGetPosLocation } = usePaymentConfig();
  const { posLocationList } = paymentConfig;

  const { virtualTerminal, fetchLocationsList, fetchDrawersList } =
    useVirtualTerminal();
  const { unpaidTickets, fetchTickets } = useUnpaidTickets();
  const { locationsList, drawersList } = virtualTerminal;

  const [originalAmount, setOriginalAmount] = useState(0);
  const [showPaySDK, setShowPaySDK] = useState(false);

  const defaultFormValues: IVirtualTerminalForm = {
    authToken: '',
    originalAmount: 0,
    taxes: 0,
    discount: 0,
    rewardDollars: 0,
    entityId: '',
    locationId: '',
    employeeReferenceId: 'NA',
    ticketAlphaNumId: generateRandomAlphaNum(),
    ticketId: generateUUID(),
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  };
  const [paymentData, setPaymentData] =
    useState<IVirtualTerminalForm>(defaultFormValues);

  const { organisation } = useOrganisation();
  const { activeOrg } = organisation;
  const location = useLocation();
  const navigate = useNavigate();

  // Gate: SellTreez-integrated entity (entity has a `sell_treez_url` configured).
  // Controls the "Unpaid Tickets" tab and SellTreez-specific ticket/location fetching.
  const isSellTreezEntity = useMemo(
    () => !!activeOrg?.settings.sell_treez_url,
    [activeOrg?.settings.sell_treez_url]
  );
  const urlParams = useQuery(location.search);

  const fetchGuestListData = () => {
    if (
      posConfig.enabledProviders.data &&
      posConfig.enabledProviders.data.length > 0
    ) {
      toFetchGuestList(posConfig.enabledProviders.data[0].name || 'DUTCHIE');
    }
  };

  useEffect(() => {
    toFetchEnabledProviders();
    if (
      posConfig.enabledProviders.data &&
      posConfig.enabledProviders.data.length > 0
    ) {
      toFetchGuestList(posConfig.enabledProviders.data[0].name || 'DUTCHIE');
    }
    if (isSellTreezEntity) {
      fetchTickets();
    }
  }, []);

  // Gate: the entity's first enabled POS provider has the `enableGuestList`
  // configuration turned on. Controls the "Guest list" tab.
  const isGuestListEnabled =
    Array.isArray(posConfig?.enabledProviders?.data) &&
    posConfig.enabledProviders.data?.length > 0 &&
    posConfig.enabledProviders.data[0]?.configurations?.find(
      (config) => config.code === 'enableGuestList'
    )?.value === 'true';

  const locations = useMemo(() => {
    if (!isSellTreezEntity)
      return (locationsList ?? []) as VIRTUAL_TERMINAL.ILocation[];

    return (posLocationList ?? [])
      .filter(({ type }) => type === 'selltreez')
      .map(({ id, name, type }) => ({
        id,
        name,
        type,
      })) as VIRTUAL_TERMINAL.ILocation[];
  }, [isSellTreezEntity, posLocationList, locationsList]);

  const [activeTab, setActiveTab] = useState<TabKey>(
    (urlParams.get('tab')?.toLowerCase() as TabKey) || 'checkout'
  );

  const [isPersonalInfoFormOpen, setIsPersonalInfoFormOpen] = useState(false);
  const [submitToSDK, setSubmitToSDK] = useState(false);
  const [pendingSDKOpen, setPendingSDKOpen] = useState(false);
  const [locationSelected, setLocationSelected] =
    useState<VIRTUAL_TERMINAL.ILocation>();
  const [drawerSelected, setDrawerSelected] =
    useState<VIRTUAL_TERMINAL.IDrawer>();
  const [isUnpaidTicketFlow, setIsUnpaidTicketFlow] = useState(false);

  const hasFetchedDrawers = useRef(false);

  useEffect(() => {
    Promise.all([fetchLocationsList()]);
    if (isSellTreezEntity && activeTab !== 'guest list') {
      Promise.all([toGetPosLocation()]);
      if (!hasFetchedDrawers.current) {
        hasFetchedDrawers.current = true;
        fetchDrawersList();
      }
    }
  }, [activeTab]);

  const prevActiveTab = useRef(activeTab);
  useEffect(() => {
    if (
      isGuestListEnabled &&
      activeTab === 'guest list' &&
      prevActiveTab.current !== 'guest list'
    ) {
      fetchGuestListData();
    }
    prevActiveTab.current = activeTab;
  }, [activeTab, isGuestListEnabled]);

  useEffect(() => {
    if (!isGuestListEnabled || activeTab !== 'guest list') return;

    const AUTO_REFRESH_INTERVAL = 60000;
    const intervalId = setInterval(fetchGuestListData, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [activeTab, isGuestListEnabled, posConfig.enabledProviders.data]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabKey);
    navigate({
      search: QUERY.update(location.search, { tab: value }),
    });
  };

  useEffect(() => {
    const queryTabParam = urlParams.get('tab')?.toLowerCase();
    if (queryTabParam) {
      setActiveTab(queryTabParam as TabKey);
    } else {
      navigate({
        search: QUERY.update(location.search, { tab: 'checkout' }),
      });
    }
  }, [location.search]);

  const { handleSubmit, reset } = useForm<IVirtualTerminalForm>({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    setPaymentData((prev) => ({ ...prev, originalAmount }));
  }, [originalAmount]);

  useEffect(() => {
    if (pendingSDKOpen) {
      setShowPaySDK(true);
      setPendingSDKOpen(false);
    }
  }, [paymentData, pendingSDKOpen]);

  const onSubmitToSDK = (data: IVirtualTerminalForm) => {
    const tokens = accessLocalStorage.getUser();
    const entityId = accessLocalStorage.getEntityId();

    if (!locationSelected) {
      setErrorToast('Please select a location');
      setShowErrorToast(true);
    } else if (originalAmount === 0) {
      setErrorToast('Please enter a valid amount');
      setShowErrorToast(true);
    } else {
      setPaymentData({
        ...paymentData,
        ...data,
        originalAmount,
        entityId,
        authToken: tokens.accessToken,
        locationId: locationSelected?.id,
        cashDrawerName: drawerSelected?.name,
        isSelltreezTicket: paymentData.ticketAlphaNumId?.length === 6,
      });
      setPendingSDKOpen(true);
    }
  };

  const onSubmit = () => {
    setSubmitToSDK(true);
    setIsPersonalInfoFormOpen(true);
  };

  const handleGuestSelect = (guestData: POS_CONFIG.IGuestListData) => {
    setPaymentData({
      ...paymentData,
      firstName: guestData.firstName || '',
      lastName: guestData.lastName || '',
      phone: guestData.phone ? formatPhoneNumber(guestData.phone) : '',
    });
  };

  const handleTicketSelect = (ticketData: any) => {
    fetchDrawersList();

    const tokens = accessLocalStorage.getUser();
    const entityId = accessLocalStorage.getEntityId();
    const formData: IVirtualTerminalForm = {
      originalAmount: ticketData.grossAmount,
      taxes: 0,
      discount: 0,
      rewardDollars: 0,
      entityId,
      authToken: tokens.accessToken,
      locationId: locationsList?.[0]?.id || '',
      employeeReferenceId: '',
      ticketAlphaNumId: ticketData.orderNumber,
      ticketId: ticketData.orderId,
      firstName: ticketData.customerName.split(' ')[0] || '',
      lastName: ticketData.customerName.split(' ').slice(1).join(' ') || '',
      phone: formatPhoneNumber(ticketData.customerPhone) || '',
      email: ticketData.customerEmail || '',
      isSelltreezTicket: ticketData.orderNumber?.length === 6,
      ticketStatus: ticketData.status || null,
      orderType: ticketData.orderType || null,
    };
    reset(formData);
    setOriginalAmount(ticketData.grossAmount);
    setPaymentData(formData);

    if (drawersList.length > 0) {
      let drawerToAssign: VIRTUAL_TERMINAL.IDrawer | undefined;
      if (ticketData.cashDrawerName) {
        drawerToAssign = drawersList.find(
          (d) => d.name === ticketData.cashDrawerName
        );
      }
      if (!drawerToAssign) {
        drawerToAssign = drawersList.find((d) => d.isActive);
      }
      setDrawerSelected(drawerToAssign);
    } else {
      setDrawerSelected(undefined);
    }
  };

  const onResetVirtualTerminal = () => {
    const newDefaultValues = {
      ...defaultFormValues,
      ticketAlphaNumId: generateRandomAlphaNum(),
      ticketId: generateUUID(),
    };
    reset(newDefaultValues);
    setOriginalAmount(0);
    setPendingSDKOpen(false);
    setPaymentData(newDefaultValues);
    setIsUnpaidTicketFlow(false);
    setDrawerSelected(undefined);
  };

  useEffect(() => {
    if (isNonSTRefId(paymentData.ticketAlphaNumId)) {
      setDrawerSelected(undefined);
    }
  }, [paymentData.ticketAlphaNumId]);

  useEffect(() => {
    if (!isUnpaidTicketFlow && submitToSDK) {
      onResetVirtualTerminal();
      setOriginalAmount(0);
    }
  }, [isUnpaidTicketFlow]);

  const onLocationSelect = (locationData: VIRTUAL_TERMINAL.ILocation) => {
    setLocationSelected(locationData);
    localStorage.setItem('vt_location', locationData.id);
  };

  const onDrawerSelect = (drawerData: VIRTUAL_TERMINAL.IDrawer) => {
    setDrawerSelected(drawerData);
    localStorage.setItem('vt_drawer', drawerData.id);
  };

  useEffect(() => {
    if (Array.isArray(locations) && locations.length > 0 && !locationSelected) {
      const saved = locations?.find(
        (item: VIRTUAL_TERMINAL.ILocation) =>
          localStorage.getItem('vt_location') === item.id
      );
      if (saved) setLocationSelected(saved);
    }
  }, [locations, locationSelected]);

  useEffect(() => {
    if (
      Array.isArray(drawersList) &&
      drawersList.length > 0 &&
      !drawerSelected
    ) {
      const saved = drawersList?.find(
        (item: VIRTUAL_TERMINAL.IDrawer) =>
          localStorage.getItem('vt_drawer') === item.id
      );
      if (saved) setDrawerSelected(saved);
    }
  }, [drawersList]);

  const handleShowToast = (message: string, type: ToastType) => {
    if (type === ToastType.success) {
      setSuccessToast(message);
      setShowSuccessToast(true);
      if (successToastTimerRef.current) {
        clearTimeout(successToastTimerRef.current);
      }
      successToastTimerRef.current = setTimeout(() => {
        setShowSuccessToast(false);
      }, 3000);
    } else {
      setErrorToast(message);
      setShowErrorToast(true);
    }
  };

  // Tab visibility (`show`) — all of these require `hasVtAccess` first (see below):
  //   - Checkout        → always shown
  //   - Guest list      → isGuestListEnabled (POS `enableGuestList` config on)
  //   - Unpaid Tickets  → isSellTreezEntity (entity has a sell_treez_url)
  //   - Recent Payments → always shown
  const TABS: VtTabConfig[] = [
    {
      value: 'checkout',
      label: 'Checkout',
      show: true,
      render: () => (
        <>
          <CheckoutTab
            paymentData={paymentData}
            setPaymentData={setPaymentData}
            originalAmount={originalAmount}
            setOriginalAmount={setOriginalAmount}
            isPersonalInfoFormOpen={isPersonalInfoFormOpen}
            setIsPersonalInfoFormOpen={setIsPersonalInfoFormOpen}
            submitToSDK={submitToSDK}
            setSubmitToSDK={setSubmitToSDK}
            isUnpaidTicketFlow={isUnpaidTicketFlow}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            onSubmitToSDK={onSubmitToSDK}
            locations={locations}
            locationSelected={locationSelected}
            drawers={drawersList}
            drawerSelected={drawerSelected}
            isSellTreezEntity={isSellTreezEntity}
            onLocationSelect={onLocationSelect}
            onDrawerSelect={onDrawerSelect}
          />
          {paymentData && showPaySDK && (
            <PaymentCollectionDrawer
              showPaySDK={showPaySDK}
              setShowPaySDK={setShowPaySDK}
              paymentData={paymentData}
              entityName={activeOrg?.name}
              dispensaryShortName={STRING.getDispensaryShortName(
                activeOrg?.settings?.sell_treez_url,
                activeOrg?.name
              )}
              isSellTreezEntity={isSellTreezEntity}
              resetVirtualTerminal={onResetVirtualTerminal}
              onShowToast={handleShowToast}
            />
          )}
        </>
      ),
    },
    {
      value: 'guest list',
      label: 'Guest list',
      iconName: 'user',
      show: isGuestListEnabled,
      count: posConfig.guestList.data?.list?.length || 0,
      render: () => (
        <GuestListTab
          guestList={posConfig.guestList.data?.list || []}
          isLoading={isLoading}
          onGuestSelect={handleGuestSelect}
          onRefresh={fetchGuestListData}
        />
      ),
    },
    {
      value: 'unpaid tickets',
      label: 'Unpaid Tickets',
      iconName: 'ticket',
      show: isSellTreezEntity,
      count: unpaidTickets.length,
      render: () => (
        <UnpaidTicketsTab
          dateFrom={moment().startOf('day').toISOString()}
          dateTo={moment().endOf('day').toISOString()}
          isActiveTab={activeTab === 'unpaid tickets'}
          onTicketSelect={handleTicketSelect}
          setIsUnpaidTicketFlow={setIsUnpaidTicketFlow}
        />
      ),
    },
    {
      value: 'recent payments',
      label: 'Recent Payments',
      iconName: 'dollar',
      show: true,
      render: () => (
        <RecentPayments isActiveTab={activeTab === 'recent payments'} />
      ),
    },
  ];

  const visibleTabs = TABS.filter((t) => t.show);

  // Master access gate for the whole Virtual Terminal page. The active entity must
  // carry the `tp::virtualTerminalAccess` permission in its mso-treez-pay permission
  // set; otherwise the <Restricted /> banner is shown instead of the tabs.
  // NOTE: this is an advisory UI gate — the backend enforces access independently.
  const entityPermissions =
    paymentConfig?.msoPermissionsList?.entityContexts?.find(
      (entity) => entity.id === activeOrg?.treez_entity_id
    );
  const hasVtAccess = Boolean(
    entityPermissions?.permissions?.includes('tp::virtualTerminalAccess')
  );

  return (
    <Card className="border-0 shadow-none">
      <PageHeading
        heading="Virtual Terminal"
        subHeading="Process virtual terminal transactions"
        className="hidden sm:block mb-5"
      />
      <div className="block sm:hidden mb-3">
        <div className="rounded-b bg-light-green-bg flex justify-center items-center px-2.5 py-1.5 gap-2.5">
          <span className="text-dark-green-link text-xs font-semibold leading-4">
            Virtual terminal
          </span>
        </div>
      </div>
      {hasVtAccess ? (
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <div className="bg-white -mx-8 pb-4 max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:w-screen max-sm:z-10 max-sm:mx-0">
            <TabsList
              className={cn(
                'w-full justify-start rounded-none border-b bg-transparent p-0 h-auto gap-5 px-7',
                'flex-nowrap overflow-x-auto',
                'max-sm:border-b-0 max-sm:border-t max-sm:justify-center max-sm:pb-6'
              )}
            >
              {visibleTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  data-testid={`${tab.value}-tab`}
                  id={`${tab.value}-tab`}
                  className={cn(
                    'rounded-none border-b-2 border-transparent whitespace-nowrap',
                    'data-[state=active]:border-treez-green data-[state=active]:text-gray-blue-80 data-[state=active]:bg-transparent data-[state=active]:shadow-none',
                    'px-0 text-muted-foreground hover:text-gray-blue-80 flex items-center gap-2'
                  )}
                >
                  {tab.iconName && (
                    <Icon name={tab.iconName} size={16} aria-hidden="true" />
                  )}
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <Badge
                      variant="default"
                      className="ml-1 bg-green-400 text-white"
                    >
                      {tab.count}
                    </Badge>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {visibleTabs.map((tab) => (
            <TabsContent
              key={tab.value}
              value={tab.value}
              className="mt-4 xl:min-w-200 max-md:min-w-0 max-md:px-5 max-md:mb-16"
            >
              {tab.render()}
            </TabsContent>
          ))}

          {showErrorToast && (
            <ToastContainer
              type={ToastType.error}
              showToast={showErrorToast}
              onClose={() => setShowErrorToast(false)}
              message={errorToast}
              showCross
            />
          )}
          {showSuccessToast && (
            <ToastContainer
              type={ToastType.success}
              showToast={showSuccessToast}
              onClose={() => setShowSuccessToast(false)}
              message={successToast}
              showCross
            />
          )}
        </Tabs>
      ) : (
        <Restricted />
      )}
    </Card>
  );
};

export default VirtualTerminals;
